import { err, ok, type Result } from '@aibi/shared';
import type { AIDiagnostic, AIErrorCode, AIValidationStage } from '../errors.js';
import { scanValue } from '../guardrails/content-guards.js';
import { DEFAULT_AI_CORE_LIMITS, type AICoreLimits } from '../limits.js';
import { AIModelOutputSchema, type AIModelOutput } from '../protocol.js';

export const diagnostic = (
  stage: AIValidationStage,
  code: AIErrorCode,
  path: string,
  message: string,
  actionId?: string,
): AIDiagnostic => ({
  stage,
  code,
  path,
  message,
  ...(actionId === undefined ? {} : { actionId }),
});

const FENCED_JSON = /^```(?:json)?\s*([\s\S]*?)\s*```$/;

/**
 * Etapas independientes del modelo semantico: tamano -> JSON -> guardrails -> esquema
 * estricto del protocolo. Las comparten `validateAIOutput` (navegador) y el relay del
 * servidor, que la usa como pre-validacion antes de devolver la respuesta de un proveedor.
 *
 * @example
 * const parsed = parseAIModelOutput(rawFromProvider);
 * if (!parsed.ok) reject(parsed.error);
 */
export function parseAIModelOutput(
  raw: string,
  limits: Pick<AICoreLimits, 'maxResponseLength'> = DEFAULT_AI_CORE_LIMITS,
): Result<AIModelOutput, AIDiagnostic[]> {
  // 1. Tamano
  if (raw.length > limits.maxResponseLength) {
    return err([
      diagnostic(
        'size',
        'LIMIT_EXCEEDED',
        '',
        `La respuesta supera ${String(limits.maxResponseLength)} caracteres.`,
      ),
    ]);
  }

  // 2. JSON (sin evaluar nada: solo JSON.parse)
  const trimmed = raw.trim();
  const body = FENCED_JSON.exec(trimmed)?.[1] ?? trimmed;
  let parsed: unknown;
  try {
    parsed = JSON.parse(body);
  } catch {
    return err([diagnostic('parse', 'INVALID_SCHEMA', '', 'La respuesta no es JSON valido.')]);
  }
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return err([
      diagnostic('parse', 'INVALID_SCHEMA', '', 'La respuesta debe ser un objeto JSON.'),
    ]);
  }

  // 3. Guardrails de contenido
  const findings = scanValue(parsed);
  if (findings.length > 0) {
    const unique = new Map(findings.map((finding) => [`${finding.path}|${finding.kind}`, finding]));
    return err(
      [...unique.values()].map((finding) =>
        diagnostic(
          'guardrails',
          'UNSAFE_CONTENT',
          finding.path,
          `Contenido no permitido (${finding.kind}).`,
        ),
      ),
    );
  }

  // 4. Esquema estricto del protocolo
  const schema = AIModelOutputSchema.safeParse(parsed);
  if (!schema.success) {
    return err(
      schema.error.issues.map((issue) =>
        diagnostic('schema', 'INVALID_SCHEMA', issue.path.map(String).join('.'), issue.message),
      ),
    );
  }
  return ok(schema.data);
}
